import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { GalleryCard } from '../components/GalleryCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useGalleries } from '../hooks/useGalleries';
import { PhotoStreamClient } from '../api/PhotoStreamClient';
import { Gallery } from '../api/types';

const apiClient = new PhotoStreamClient(
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'
);

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { galleries, loading, error, loadGalleries } = useGalleries(apiClient);

  useEffect(() => {
    loadGalleries();
  }, [loadGalleries]);

  const handleGalleryClick = (gallery: Gallery) => {
    navigate(`/gallery/${gallery.id}`);
  };

  const handleRetry = () => {
    loadGalleries();
  };

  return (
    <div className="container mx-auto py-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2 text-primary">PhotoStream</h1>
        <p className="text-muted-foreground">Share and view photo galleries</p>
      </div>

      {error && (
        <Card className="max-w-md mx-auto mb-6 border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleRetry} variant="outline" className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {loading && <LoadingSpinner />}

      {!loading && !error && galleries.length === 0 && (
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>No Galleries Found</CardTitle>
            <CardDescription>
              Create your first gallery to get started.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href="/admin">Create Gallery</a>
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && galleries.length > 0 && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">
              Galleries ({galleries.length})
            </h2>
            <Button variant="outline" asChild>
              <a href="/admin">Create New</a>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.map((gallery) => (
              <GalleryCard
                key={gallery.id}
                gallery={gallery}
                onClick={handleGalleryClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
